import type { FiatPrice, Reservation } from "@b2bportal/lodging-api";

/**
 * Normalized pay-at-property amounts as RAW `FiatPrice`s (never formatted here,
 * never derived/summed client-side). The hook owns `formatFee`/`hasPayAtProperty`.
 */
export type PayAtProperty = {
  resortFees?: FiatPrice;
  mandatoryFee?: FiatPrice;
  mandatoryTax?: FiatPrice;
  feeTotal?: FiatPrice;
};

/**
 * Resolve the pay-at-property breakdown, `pricingV2`-first (FTC/EAN-compliant).
 * When `pricingV2` is absent (historical reservations booked before the BP-5011
 * rollout) delegate to the documented legacy fallback. All amounts are
 * pre-computed by the backend — never derive prices here.
 */
export const getPayAtPropertyBreakdown = (res: Reservation): PayAtProperty => {
  if (res.pricingV2 == null) {
    return getLegacyPayAtPropertyBreakdown(res);
  }

  const dueAtProperty = res.pricingV2.taxesAndFeesDueAtProperty;
  // `value` is typed required, but guard it anyway — a backend nulling a
  // present bucket's amount must degrade to "omitted line", never crash the
  // reservation render.
  return {
    resortFees: dueAtProperty?.resortFees?.value?.fiat,
    mandatoryFee: dueAtProperty?.mandatoryFees?.value?.fiat,
    mandatoryTax: dueAtProperty?.mandatoryTaxes?.value?.fiat,
    feeTotal: dueAtProperty?.total?.value?.fiat,
  };
};

/**
 * LEGACY — kept only because `Reservation.pricingV2` is populated for NEW bookings
 * only; historical reservations still rely on the legacy fee/tax breakdown.
 * Restricted to the 3 total-composing buckets (RBC-2137 cityTax-dup stopgap).
 * Remove once all reservations carry `pricingV2` (BP-5011 follow-up).
 */
export const getLegacyPayAtPropertyBreakdown = (
  res: Reservation
): PayAtProperty => ({
  resortFees: res.pricing.feeBreakdown?.resortFees,
  mandatoryFee: res.pricing.feeBreakdown?.mandatoryFee,
  feeTotal: res.pricing.feeBreakdown?.total,
  // mandatoryTax comes from taxBreakdown (Taxes), whose fields are Prices (use .fiat)
  mandatoryTax: res.pricing.taxBreakdown?.mandatoryTax?.fiat,
});
